import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  CalendarDays, Plus, Users, Loader2, CheckCircle2, Mail, Pencil, XCircle,
  Search, CheckSquare, Square, Video, DoorOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/config';

const fmtDate = (iso) =>
  new Date(iso).toLocaleString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });

const toLocalInput = (iso) => {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).formatToParts(d).reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

const EMPTY_FORM = { title: '', description: '', event_date: '', duration_minutes: 60, meeting_url: '', targetAll: true, target_manager_ids: [] };

export default function AdminEventiFormazione() {
  const token = localStorage.getItem('hw_token');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [managers, setManagers] = useState([]);
  const [search, setSearch] = useState('');
  const [presenze, setPresenze] = useState(null);
  const [loadingPresenze, setLoadingPresenze] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/training-events`, { headers });
      if (res.ok) setEvents(await res.json());
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [token]); // eslint-disable-line

  useEffect(() => {
    fetchEvents();
    fetch(`${API}/api/tickets/managers-list`, { headers })
      .then((r) => r.json()).then((d) => setManagers(Array.isArray(d) ? d : (d.managers || [])))
      .catch(() => {});
  }, [fetchEvents]); // eslint-disable-line

  const openNew = () => { setEditingId(null); setForm(EMPTY_FORM); setShowForm(true); };

  const openEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title, description: ev.description || '',
      event_date: toLocalInput(ev.event_date),
      duration_minutes: ev.duration_minutes || 60,
      meeting_url: ev.meeting_url || '',
      targetAll: !ev.target_manager_ids || ev.target_manager_ids.length === 0,
      target_manager_ids: ev.target_manager_ids || []
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.event_date || !form.meeting_url.trim()) {
      toast.error('Titolo, data e link riunione sono obbligatori');
      return;
    }
    if (!form.targetAll && form.target_manager_ids.length === 0) {
      toast.error('Seleziona almeno un responsabile');
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      event_date: form.event_date,
      duration_minutes: parseInt(form.duration_minutes) || 60,
      meeting_url: form.meeting_url.trim(),
      target_manager_ids: form.targetAll ? null : form.target_manager_ids
    };
    try {
      const res = await fetch(editingId ? `${API}/api/training-events/${editingId}` : `${API}/api/training-events`, {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(editingId ? 'Evento aggiornato' : `Evento creato — annuncio ${data.announce}`);
        setShowForm(false);
        fetchEvents();
      } else toast.error(data.detail || 'Errore nel salvataggio');
    } catch (e) { toast.error('Errore di connessione'); }
    setSaving(false);
  };

  const handleCancel = async (ev) => {
    if (!window.confirm(`Annullare l'evento "${ev.title}"? Non sarà più visibile ai responsabili.`)) return;
    try {
      const res = await fetch(`${API}/api/training-events/${ev.id}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Evento annullato'); fetchEvents(); }
    } catch (e) { toast.error('Errore'); }
  };

  const openPresenze = async (ev) => {
    setLoadingPresenze(true);
    setPresenze({ event: ev, rows: [] });
    try {
      const res = await fetch(`${API}/api/training-events/${ev.id}/registrations`, { headers });
      if (res.ok) setPresenze(await res.json());
    } catch (e) { toast.error('Errore caricamento presenze'); }
    setLoadingPresenze(false);
  };

  const toggleManager = (id) => {
    setForm((f) => ({
      ...f,
      target_manager_ids: f.target_manager_ids.includes(id)
        ? f.target_manager_ids.filter((x) => x !== id)
        : [...f.target_manager_ids, id]
    }));
  };

  const filteredManagers = managers.filter((m) =>
    `${m.first_name || ''} ${m.last_name || ''} ${m.email || ''}`.toLowerCase().includes(search.toLowerCase()));

  const EmailBadge = ({ ok, queued, label }) => (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${ok ? 'bg-green-100 text-green-700' : queued ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400'}`}>
      <Mail className="h-3 w-3" />{label}{queued && !ok ? ' · in coda' : ''}
    </span>
  );

  return (
    <Layout title="Eventi e Riunioni">
      <div className="space-y-6" data-testid="admin-eventi-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1E3A8A] flex items-center gap-2">
              <CalendarDays className="h-6 w-6" /> Eventi e Riunioni — Formazione
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Email automatiche: annuncio a 30 giorni, promemoria il giorno prima, accesso accodato nell'ora precedente. Pulsante attivo da 15 minuti prima.
            </p>
          </div>
          <Button onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white" data-testid="new-event-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuovo Evento
          </Button>
        </div>

        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#1E3A8A]" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-events-table">
                <thead className="bg-[#1E3A8A]">
                  <tr>
                    {['Data', 'Evento', 'Durata', 'Destinatari', 'Registrati', 'Presenti', 'Email', 'Azioni'].map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-white uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev.id} className={`border-b border-slate-100 ${ev.status === 'cancelled' ? 'opacity-50' : ''}`} data-testid={`admin-event-row-${ev.id}`}>
                      <td className="px-3 py-3 text-sm text-[#1E3A8A] font-medium whitespace-nowrap">{fmtDate(ev.event_date)}</td>
                      <td className="px-3 py-3">
                        <p className="text-sm font-semibold text-[#1E3A8A]">{ev.title}</p>
                        {ev.status === 'cancelled' && <span className="text-xs text-red-600 font-semibold">ANNULLATO</span>}
                      </td>
                      <td className="px-3 py-3 text-sm text-[#1E3A8A]">{ev.duration_minutes || 60} min</td>
                      <td className="px-3 py-3 text-sm text-[#1E3A8A]">
                        {ev.target_manager_ids?.length ? `${ev.target_manager_ids.length} selezionati` : 'Tutti'}
                      </td>
                      <td className="px-3 py-3 text-sm font-bold text-[#1E3A8A]">{ev.stats?.registered || 0}</td>
                      <td className="px-3 py-3">
                        <span className="text-sm font-bold text-green-700">{ev.stats?.attended || 0}</span>
                        {ev.stats?.reentry > 0 && (
                          <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full" title="Richieste di ingresso">
                            <DoorOpen className="h-3 w-3 inline" /> {ev.stats.reentry}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-0.5">
                          <EmailBadge ok={ev.announce_sent} queued={ev.announce_queue_claimed} label="Annuncio" />
                          <EmailBadge ok={ev.reminder_sent} queued={ev.reminder_queue_claimed} label="Promemoria" />
                          <EmailBadge ok={ev.entry_email_sent} queued={ev.entry_queue_claimed} label="Ingresso" />
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => openPresenze(ev)}
                            className="border-blue-300 text-[#1E3A8A]" data-testid={`presenze-btn-${ev.id}`}>
                            <Users className="h-4 w-4" />
                          </Button>
                          {ev.status !== 'cancelled' && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => openEdit(ev)}
                                className="border-slate-300 text-slate-600" data-testid={`edit-event-btn-${ev.id}`}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleCancel(ev)}
                                className="border-red-200 text-red-600" data-testid={`cancel-event-btn-${ev.id}`}>
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {events.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-400">Nessun evento creato. Clicca "Nuovo Evento" per iniziare.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Modal creazione/modifica */}
        <Dialog open={showForm} onOpenChange={(o) => !o && setShowForm(false)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-form-modal">
            <DialogHeader>
              <DialogTitle className="text-[#1E3A8A]">{editingId ? 'Modifica evento' : 'Nuovo evento di formazione'}</DialogTitle>
              <DialogDescription>Compila i dettagli: le email ai responsabili partono in automatico.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Titolo *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Es. Nuova sessione di Formazione" data-testid="event-title-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Data e ora (Italia) *</Label>
                  <Input type="datetime-local" value={form.event_date}
                    onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="event-date-input" />
                </div>
                <div>
                  <Label>Durata (minuti)</Label>
                  <Input type="number" min="15" step="15" value={form.duration_minutes}
                    onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="event-duration-input" />
                </div>
              </div>
              <div>
                <Label>Link riunione (Meet / Zoom / Teams) *</Label>
                <Input value={form.meeting_url} onChange={(e) => setForm({ ...form, meeting_url: e.target.value })}
                  placeholder="https://meet.google.com/..." data-testid="event-url-input" />
                <p className="text-xs text-slate-500 mt-1">Il link reale non viene mai mostrato: i responsabili entrano tramite link personale monouso.</p>
              </div>
              <div>
                <Label>Descrizione</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Argomenti, relatori, note..." data-testid="event-desc-input" />
              </div>
              <div>
                <Label className="mb-2 block">Destinatari</Label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2 text-sm text-[#1E3A8A] cursor-pointer">
                    <input type="radio" checked={form.targetAll} onChange={() => setForm({ ...form, targetAll: true })} data-testid="target-all-radio" />
                    Tutti i responsabili attivi
                  </label>
                  <label className="flex items-center gap-2 text-sm text-[#1E3A8A] cursor-pointer">
                    <input type="radio" checked={!form.targetAll} onChange={() => setForm({ ...form, targetAll: false })} data-testid="target-select-radio" />
                    Seleziona ({form.target_manager_ids.length})
                  </label>
                </div>
                {!form.targetAll && (
                  <div className="border border-slate-200 rounded-lg">
                    <div className="p-2 border-b border-slate-100 relative">
                      <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input className="pl-8 h-8" placeholder="Cerca responsabile..." value={search}
                        onChange={(e) => setSearch(e.target.value)} data-testid="manager-search-input" />
                    </div>
                    <div className="max-h-44 overflow-y-auto">
                      {filteredManagers.map((m) => (
                        <button key={m.id} type="button" onClick={() => toggleManager(m.id)}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-blue-50"
                          data-testid={`manager-pick-${m.id}`}>
                          {form.target_manager_ids.includes(m.id)
                            ? <CheckSquare className="h-4 w-4 text-[#1D4ED8]" />
                            : <Square className="h-4 w-4 text-slate-300" />}
                          <span className="text-[#1E3A8A]">{m.first_name} {m.last_name}</span>
                          <span className="text-xs text-slate-400 truncate">{m.email}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-blue-50 border-l-4 border-[#1D4ED8] rounded p-3 text-xs text-[#1E3A8A]">
                Alla creazione: se mancano <strong>meno di 30 giorni</strong> l'annuncio con pulsante "Registrati" viene accodato <strong>subito</strong>, altrimenti a 30 giorni dall'evento. Promemoria il giorno prima; accesso accodato nell'ora precedente e utilizzabile da 15 minuti prima.
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white" data-testid="save-event-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingId ? 'Salva modifiche' : 'Crea evento')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal presenze */}
        <Dialog open={!!presenze} onOpenChange={(o) => !o && setPresenze(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="presenze-modal">
            <DialogHeader>
              <DialogTitle className="text-[#1E3A8A] flex items-center gap-2">
                <Users className="h-5 w-5" /> Registro presenze — {presenze?.event?.title}
              </DialogTitle>
              <DialogDescription>Registrazioni, presenze effettive e richieste di ingresso.</DialogDescription>
            </DialogHeader>
            {loadingPresenze ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#1E3A8A]" /></div>
            ) : (
              <>
                <div className="flex gap-4 text-sm text-[#1E3A8A]">
                  <span><strong>{presenze?.rows?.filter((r) => r.registered).length || 0}</strong> registrati</span>
                  <span className="text-green-700"><strong>{presenze?.rows?.filter((r) => r.attended).length || 0}</strong> presenti</span>
                  <span className="text-slate-500">{presenze?.rows?.length || 0} invitati totali</span>
                </div>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-blue-50">
                      <tr>
                        {['Responsabile', 'Registrato', 'Presente', 'Ingresso da', 'Richiesta ingresso'].map((h) => (
                          <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-[#1E3A8A] uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {presenze?.rows?.map((r) => (
                        <tr key={r.manager_id} className="border-t border-slate-100">
                          <td className="px-3 py-2">
                            <p className="font-medium text-[#1E3A8A]">{r.name || '—'}</p>
                            <p className="text-xs text-slate-400">{r.email}</p>
                          </td>
                          <td className="px-3 py-2">
                            {r.registered
                              ? <span className="text-green-700 text-xs font-semibold flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Sì</span>
                              : <span className="text-slate-400 text-xs">No</span>}
                          </td>
                          <td className="px-3 py-2">
                            {r.attended
                              ? <span className="text-green-700 text-xs font-semibold flex items-center gap-1"><Video className="h-3.5 w-3.5" /> Presente</span>
                              : <span className="text-slate-400 text-xs">—</span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-[#1E3A8A]">
                            {r.joined_from === 'email' ? 'Email' : r.joined_from === 'platform' ? 'HW Smart' : '—'}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {r.reentry_requested_at
                              ? <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">{fmtDate(r.reentry_requested_at)}</span>
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
